import { useRef, useState, useEffect } from 'react'
import { motion, useScroll, useTransform } from 'framer-motion'
import { Link } from 'react-router-dom'
import { ArrowRight, Award, Sparkles, Briefcase, Clock, CheckCircle, Phone, Users } from 'lucide-react'
import { apiFetch } from '../config/api'

interface JobPosting {
  id: number
  title: string
  description: string
  requirements: string | null
  location: string | null
  employment_type: string | null
  department: string | null
  status: 'Aktiv' | 'Inaktiv' | 'Geschlossen'
  created_at: string
  updated_at: string
}

// Benefits
const whyUs = [
  { icon: Users, title: 'Familiäres Team', desc: 'Arbeiten in freundlicher Atmosphäre' },
  { icon: Clock, title: 'Flexible Zeiten', desc: 'Arbeit, die zu Ihrem Leben passt' },
  { icon: Award, title: 'Faire Vergütung', desc: 'Attraktive Bezahlung garantiert' },
  { icon: Sparkles, title: 'Besondere Events', desc: 'Unvergessliche Momente mitgestalten' }
]

export default function Jobs() {
  const heroRef = useRef<HTMLDivElement>(null)
  const { scrollYProgress } = useScroll()
  const heroOpacity = useTransform(scrollYProgress, [0, 0.15], [1, 0])
  const heroScale = useTransform(scrollYProgress, [0, 0.15], [1, 1.1])
  
  const [jobs, setJobs] = useState<JobPosting[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const fetchJobs = async () => {
      try {
        setLoading(true)
        const response = await apiFetch('/jobs', {
          method: 'GET',
        })

        const data = await response.json()

        if (response.ok && data.success) {
          // Nur aktive Jobs anzeigen
          const activeJobs = (data.data || []).filter((job: JobPosting) => job.status === 'Aktiv')
          setJobs(activeJobs)
        } else {
          console.error('Fehler beim Laden der Stellenanzeigen:', data.error)
          setJobs([])
        }
      } catch (err) {
        console.error('Verbindungsfehler:', err)
        setJobs([])
      } finally {
        setLoading(false)
      }
    }

    fetchJobs()
  }, [])

  return (
    <div className="bg-[#0a0a0a]">
      
      {/* ════════════════════════════════════════════════════════════
          HERO SECTION
      ════════════════════════════════════════════════════════════ */}
      <section ref={heroRef} className="relative h-[50vh] min-h-[400px] max-h-[550px] w-full overflow-hidden">
        <motion.div style={{ scale: heroScale }} className="absolute inset-0">
          <img
            src="/assets/heros/hero_bilder.jpg"
            alt="Jobs bei Hückels May"
            className="absolute inset-0 w-full h-full object-cover"
            style={{ filter: 'brightness(0.3)' }}
          />
        </motion.div>

        <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-transparent to-[#0a0a0a]" />
        <div className="absolute top-0 left-1/4 w-[400px] h-[400px] bg-brand-800/10 rounded-full blur-[120px]" />
        
        <motion.div 
          style={{ opacity: heroOpacity }}
          className="relative z-10 h-full flex flex-col justify-center items-center text-center px-6 pt-16"
        >
          <motion.div
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="mb-4"
          >
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/5 backdrop-blur-xl border border-white/10 rounded-full">
              <Briefcase className="w-3 h-3 text-brand-400" />
              <span className="text-white/70 text-xs uppercase tracking-[0.25em]">Karriere</span>
            </div>
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.3 }}
            className="font-serif text-3xl md:text-4xl lg:text-5xl text-white font-light tracking-tight leading-[1.1] mb-4"
          >
            Jobs & <span className="italic text-white/70">Karriere</span>
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.5 }}
            className="text-white/50 text-sm md:text-base max-w-xl leading-relaxed"
          >
            Werden Sie Teil unseres Teams und gestalten Sie unvergessliche Events mit.
          </motion.p>
        </motion.div>
      </section>

      {/* ════════════════════════════════════════════════════════════
          WHY US
      ════════════════════════════════════════════════════════════ */}
      <section className="border-y border-white/5">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-2 md:grid-cols-4">
            {whyUs.map((item, index) => (
              <motion.div
                key={item.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="py-10 md:py-12 px-6 text-center border-r border-white/5 last:border-r-0 group hover:bg-white/[0.02] transition-colors flex flex-col items-center justify-center h-[140px]"
              >
                <div className="inline-flex items-center justify-center w-10 h-10 rounded-xl bg-brand-800/10 border border-brand-800/20 mb-3 group-hover:scale-110 transition-transform">
                  <item.icon className="w-4 h-4 text-brand-400" />
                </div>
                <p className="text-white text-sm font-medium mb-1">{item.title}</p>
                <p className="text-white/40 text-xs">{item.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ════════════════════════════════════════════════════════════
          JOB LISTINGS
      ════════════════════════════════════════════════════════════ */}
      <section className="relative py-24 md:py-32">
        <div className="absolute top-40 right-20 w-[500px] h-[500px] bg-brand-800/5 rounded-full blur-[120px]" />
        
        <div className="max-w-6xl mx-auto px-6 md:px-8 lg:px-12 relative z-10">
          {/* Header */}
          <div className="text-center mb-16 md:mb-20">
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-brand-400 text-xs uppercase tracking-[0.4em] mb-4"
            >
              Offene Stellen
            </motion.p>
            <motion.h2
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="font-serif text-3xl md:text-4xl lg:text-5xl text-white font-light mb-4"
            >
              Aktuelle <span className="italic text-white/60">Stellenangebote</span>
            </motion.h2>
          </div>

          {/* Job Cards */}
          {loading ? (
            <div className="text-center py-16 md:py-20">
              <motion.div
                animate={{ rotate: 360 }}
                transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                className="inline-block mb-4"
              >
                <Briefcase className="h-8 w-8 text-brand-400" />
              </motion.div>
              <p className="text-white/50">Lade Stellenanzeigen...</p>
            </div>
          ) : jobs.length === 0 ? (
            <div className="text-center py-16 md:py-20">
              <Briefcase className="h-12 w-12 text-white/30 mx-auto mb-4" />
              <p className="text-white/50">Aktuell sind keine Stellenanzeigen verfügbar.</p>
            </div>
          ) : (
            <div className="space-y-8 md:space-y-10">
              {jobs.map((job, index) => (
                <motion.div
                  key={job.id}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  className="group"
                >
                  <div className="p-8 md:p-10 lg:p-12 bg-white/[0.02] border border-white/5 rounded-2xl hover:border-brand-800/30 transition-all duration-500">
                    <div className="grid lg:grid-cols-3 gap-8 lg:gap-10">
                      {/* Main Content */}
                      <div className="lg:col-span-2 space-y-6">
                        <div className="h-px w-12 bg-brand-800 mb-6 group-hover:w-20 transition-all duration-500" />
                        
                        <h3 className="font-serif text-2xl md:text-3xl lg:text-4xl text-white font-light leading-tight group-hover:text-brand-400 transition-colors duration-500">
                          {job.title}
                        </h3>
                        
                        <div className="space-y-4">
                          <p className="text-white/50 leading-relaxed text-base md:text-lg">{job.description}</p>
                          
                          {/* Requirements */}
                          {job.requirements && (
                            <div className="pt-2">
                              <p className="text-white/40 text-sm font-medium mb-3 uppercase tracking-wider">Anforderungen:</p>
                              <p className="text-white/50 text-sm md:text-base leading-relaxed whitespace-pre-line">{job.requirements}</p>
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Sidebar */}
                      <div className="lg:pt-6 space-y-4">
                        {/* Department */}
                        {job.department && (
                          <div className="p-5 bg-white/[0.02] border border-white/10 rounded-xl hover:bg-white/[0.03] transition-colors">
                            <div className="flex items-start gap-4">
                              <div className="flex-shrink-0 mt-0.5">
                                <Award className="w-5 h-5 text-brand-400" />
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="text-white/40 text-xs uppercase tracking-wider mb-2">Abteilung</p>
                                <p className="text-white font-medium text-sm md:text-base break-words">{job.department}</p>
                              </div>
                            </div>
                          </div>
                        )}

                        {/* Employment Type */}
                        {job.employment_type && (
                          <div className="p-5 bg-white/[0.02] border border-white/10 rounded-xl hover:bg-white/[0.03] transition-colors">
                            <div className="flex items-start gap-4">
                              <div className="flex-shrink-0 mt-0.5">
                                <Clock className="w-5 h-5 text-brand-400" />
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="text-white/40 text-xs uppercase tracking-wider mb-2">Beschäftigungsart</p>
                                <p className="text-white font-medium text-sm md:text-base break-words">{job.employment_type}</p>
                              </div>
                            </div>
                          </div>
                        )}

                        {/* Location */}
                        {job.location && (
                          <div className="p-5 bg-white/[0.02] border border-white/10 rounded-xl hover:bg-white/[0.03] transition-colors">
                            <div className="flex items-start gap-4">
                              <div className="flex-shrink-0 mt-0.5">
                                <Briefcase className="w-5 h-5 text-brand-400" />
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="text-white/40 text-xs uppercase tracking-wider mb-2">Standort</p>
                                <p className="text-white font-medium text-sm md:text-base break-words">{job.location}</p>
                              </div>
                            </div>
                          </div>
                        )}

                        {/* CTA */}
                        <div className="pt-2">
                          <Link to="/kontakt">
                            <motion.button
                              whileHover={{ scale: 1.02 }}
                              whileTap={{ scale: 0.98 }}
                              className="w-full px-6 py-4 md:py-5 bg-brand-800 text-white text-xs md:text-sm uppercase tracking-[0.2em] font-bold rounded-full hover:bg-brand-700 transition-all duration-500 flex items-center justify-center gap-3 shadow-lg shadow-brand-800/20"
                            >
                              Jetzt bewerben
                              <ArrowRight className="w-4 h-4 md:w-5 md:h-5" />
                            </motion.button>
                          </Link>
                        </div>
                      </div>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* ════════════════════════════════════════════════════════════
          CTA SECTION
      ════════════════════════════════════════════════════════════ */}
      <section className="py-12 md:py-20 border-t border-white/5">
        <div className="max-w-3xl mx-auto px-6 text-center relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-brand-800/10 border border-brand-800/20 mb-8">
              <Sparkles className="w-7 h-7 text-brand-400" />
            </div>
            
            <p className="text-brand-400 text-xs uppercase tracking-[0.4em] mb-6">Interesse?</p>
            
            <h2 className="font-serif text-3xl md:text-5xl text-white font-light mb-8 leading-tight">
              Wir freuen uns auf<br />
              <span className="italic text-white/60">Ihre Bewerbung</span>
            </h2>
            
            <p className="text-white/40 text-lg mb-10 max-w-2xl mx-auto">
              Werden Sie Teil unseres Teams und erleben Sie, wie Tradition auf moderne Exzellenz trifft.
            </p>

            <Link to="/kontakt">
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.98 }}
                className="px-10 py-4 bg-white text-black text-sm uppercase tracking-[0.2em] font-bold rounded-full hover:bg-brand-800 hover:text-white transition-all duration-500 flex items-center gap-3 mx-auto"
              >
                <Phone className="w-4 h-4" />
                Kontakt aufnehmen
              </motion.button>
            </Link>
          </motion.div>
        </div>
      </section>

    </div>
  )
}
